import React, { useEffect, useState, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import useApi from '../Api'
import '../styles/pages/DestinationPage.css'
import Tabs from '../components/destination_page/Tabs'
import TabPanel from '../components/destination_page/TabPanel'
import {
  FaBookOpen,
  FaBook,
  FaMapMarkerAlt,
  FaMapMarkedAlt,
  FaGlobe,
  FaInfoCircle
} from 'react-icons/fa'
import { useAuth } from '../AuthContext'
import OverviewSection from './DestinationPage/OverviewSection'
import WorldMapSection from '../components/blog_page/WorldMapSection'
import GuideSection from './DestinationPage/GuideSection'
import SavedGuidesSection from './DestinationPage/SavedGuidesSection'
import ItinerarySection from './DestinationPage/ItinerarySection'
import SavedItinerarySection from './DestinationPage/SavedItinerarySection'

const DestinationPage = () => {
  const { origin_id } = useParams()
  const location = useLocation()
  const apiClient = useApi()
  const { user, loading: authLoading } = useAuth()
  const isUserVerified = user?.emailVerified === true
  const guidesCacheRef = useRef({})
  const userGuidesCacheRef = useRef({})
  const publicItinerariesCacheRef = useRef({})
  const savedItinerariesCacheRef = useRef({})
  const [destination, setDestination] = useState(null)
  const [loadingDest, setLoadingDest] = useState(true)
  const [errorDest, setErrorDest] = useState(null)
  const [overviewData, setOverviewData] = useState(null)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [overviewError, setOverviewError] = useState(null)

  useEffect(() => {
    let active = true
    ;(async () => {
      setLoadingDest(true)
      setErrorDest(null)
      try {
        if (origin_id) {
          const resp = await apiClient.get(`/origin/${encodeURIComponent(origin_id)}`)
          if (active) setDestination(resp.data)
        } else {
          if (active) setDestination(null)
        }
      } catch {
        if (active) setErrorDest('Failed to load destination data.')
      } finally {
        if (active) setLoadingDest(false)
      }
    })()
    return () => {
      active = false
    }
  }, [origin_id, apiClient])

  useEffect(() => {
    let active = true
    ;(async () => {
      setOverviewLoading(true)
      setOverviewError(null)
      try {
        if (origin_id) {
          const resp = await apiClient.get(`/origin/${encodeURIComponent(origin_id)}/overview`)
          if (active) setOverviewData(resp.data)
        } else {
          if (active) setOverviewData(null)
        }
      } catch {
        if (active) setOverviewError('Failed to load overview.')
      } finally {
        if (active) setOverviewLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [origin_id, apiClient])

  const tabs = []
  if (origin_id) tabs.push({ label: 'Overview', icon: <FaInfoCircle /> })
  tabs.push({ label: 'WeiFinders Map', icon: <FaGlobe /> })
  tabs.push({ label: 'Guides', icon: <FaBookOpen /> })
  if (isUserVerified) {
    tabs.push(
      { label: 'Your Guides', icon: <FaBook /> },
      { label: 'Itineraries', icon: <FaMapMarkedAlt /> },
      { label: 'Your Itineraries', icon: <FaMapMarkerAlt /> }
    )
  }

  const isBrowsePage = location.pathname === '/destination/browse'
  const fallbackMetaDesc = destination
    ? `Discover guides and itineraries for ${destination.name}${
        destination.country ? `, ${destination.country}` : ''
      }. Explore travel experiences on WeiFinders.`
    : 'Browse all destinations and explore travel guides and itineraries on WeiFinders.'

  const initialLatLng =
    destination?.location?.lat != null && destination?.location?.lng != null
      ? [destination.location.lat, destination.location.lng]
      : null

  return (
    <div className="destination-page">
      <Helmet>
        <title>
          {destination
            ? `${destination.name} - Explore Guides & Itineraries | WeiFinders`
            : 'All Destinations - WeiFinders'}
        </title>
        <meta name="description" content={overviewData?.metaDescription?.description || fallbackMetaDesc} />
        <meta
          property="og:title"
          content={destination ? `${destination.name} - Explore on WeiFinders` : 'All Destinations - WeiFinders'}
        />
        <meta
          property="og:description"
          content={overviewData?.metaDescription?.description || fallbackMetaDesc}
        />
        <meta property="og:type" content="website" />
        <link
          rel="canonical"
          href={
            destination
              ? `https://weifinders.com/destination/${encodeURIComponent(origin_id)}`
              : 'https://weifinders.com/destination/browse'
          }
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta
          name="twitter:title"
          content={
            destination
              ? `${destination.name} - Explore Guides & Itineraries | WeiFinders`
              : 'All Destinations - WeiFinders'
          }
        />
        <meta
          name="twitter:description"
          content={overviewData?.metaDescription?.description || fallbackMetaDesc}
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "WeiFinders",
            "url": "https://weifinders.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://weifinders.com/search?query={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        `}</script>
      </Helmet>

      <main role="main" style={{ minHeight: '1200px' }}>
        {loadingDest || authLoading ? (
          <div className="loading-indicator" role="status" aria-live="polite">
            Loading destination...
          </div>
        ) : errorDest ? (
          <div className="error-message" role="alert">
            {errorDest}
          </div>
        ) : (
          <>
            <header className="destination-header" itemScope itemType="https://schema.org/TouristDestination">
              <h1 className="destination-title" itemProp="name">{destination?.name || 'All Destinations'}</h1>
              {destination?.country && <p className="destination-subtitle" itemProp="address">{destination.country}</p>}
            </header>

            <Tabs tabs={tabs}>
              {origin_id && (
                <TabPanel label="Overview">
                  <OverviewSection data={overviewData} loading={overviewLoading} />
                </TabPanel>
              )}

              <TabPanel label="WeiFinders Map">
                <WorldMapSection origin_id={origin_id} initialLatLng={initialLatLng} />
              </TabPanel>

              <TabPanel label="Guides">
                <GuideSection
                  origin_id={origin_id}
                  guidesCacheRef={guidesCacheRef}
                  destination={destination}
                  showCreateButtons={!isBrowsePage}
                />
              </TabPanel>

              {isUserVerified && (
                <TabPanel label="Your Guides">
                  <SavedGuidesSection
                    origin_id={origin_id}
                    userGuidesCacheRef={userGuidesCacheRef}
                    destination={destination}
                    showCreateButtons={!isBrowsePage}
                  />
                </TabPanel>
              )}

              {isUserVerified && (
                <TabPanel label="Itineraries">
                  <ItinerarySection
                    origin_id={origin_id}
                    publicItinerariesCacheRef={publicItinerariesCacheRef}
                    destination={destination}
                    showCreateButtons={!isBrowsePage}
                  />
                </TabPanel>
              )}

              {isUserVerified && (
                <TabPanel label="Your Itineraries">
                  <SavedItinerarySection
                    origin_id={origin_id}
                    savedItinerariesCacheRef={savedItinerariesCacheRef}
                    destination={destination}
                    showCreateButtons={!isBrowsePage}
                  />
                </TabPanel>
              )}
            </Tabs>
          </>
        )}
      </main>
    </div>
  )
}

export default DestinationPage
